/**
 * FornadaCombobox Component - Seletor de fornadas
 */

import { useState, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { fornadasAdapter } from "@/data";
import { Fornada } from "@/types/fornada";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface FornadaComboboxProps {
  value?: string;
  onSelect: (fornada: Fornada | null) => void;
  disabled?: boolean;
}

export function FornadaCombobox({ value, onSelect, disabled }: FornadaComboboxProps) {
  const [open, setOpen] = useState(false);
  const [fornadas, setFornadas] = useState<Fornada[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFornadas();
  }, []);

  const loadFornadas = async () => {
    try {
      setLoading(true);
      const data = await fornadasAdapter.getAll();
      setFornadas(data);
    } catch (error) {
      console.error("Erro ao carregar fornadas:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedFornada = fornadas.find((f) => f.id_fornada === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled || loading}
        >
          {selectedFornada ? (
            <span>
              #{selectedFornada.numero_fornada?.toString().padStart(6, '0') || 'N/A'} - {' '}
              {format(new Date(selectedFornada.data_fornada), "dd/MM/yyyy", { locale: ptBR })}
            </span>
          ) : (
            "Selecione uma fornada..."
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Buscar fornada..." />
          <CommandList>
            <CommandEmpty>Nenhuma fornada encontrada.</CommandEmpty>
            <CommandGroup>
              {fornadas.map((fornada) => (
                <CommandItem
                  key={fornada.id_fornada}
                  value={`${fornada.numero_fornada} ${fornada.data_fornada} ${fornada.responsavel || ''}`}
                  onSelect={() => {
                    onSelect(fornada.id_fornada === value ? null : fornada);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === fornada.id_fornada ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-semibold">
                      #FOR{fornada.numero_fornada?.toString().padStart(6, '0') || 'N/A'}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(fornada.data_fornada), "dd/MM/yyyy", { locale: ptBR })}
                      {fornada.responsavel && ` - ${fornada.responsavel}`}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
